import { useCallback, useRef, useState } from 'react';
import { type Swiper as SwiperInstance } from 'swiper';

type UseMediaPreviewerOptions = {
  total: number;
};

export const useMediaPreviewer = (options: UseMediaPreviewerOptions) => {
  const { total } = options;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);

  // ===== SWIPER =====
  const swiperRef = useRef<SwiperInstance | null>(null);
  const thumbsSwiperRef = useRef<SwiperInstance | null>(null);

  const attachSwiper = useCallback((instance: SwiperInstance | null) => {
    swiperRef.current = instance;
  }, []);

  const attachThumbsSwiper = useCallback((instance: SwiperInstance | null) => {
    thumbsSwiperRef.current = instance;
  }, []);

  const syncThumbs = useCallback((index: number) => {
    thumbsSwiperRef.current?.slideTo(index, 200);
  }, []);

  const slideToIndex = useCallback(
    (index: number) => {
      swiperRef.current?.slideTo(index);
      syncThumbs(index);
    },
    [syncThumbs]
  );

  const handleSlideChange = useCallback(
    (instance: SwiperInstance) => {
      const index = instance.activeIndex;

      setOpenedIndex((current) => {
        const newIndex = current === index ? current : index;

        syncThumbs(newIndex);

        return newIndex;
      });
    },
    [syncThumbs]
  );

  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= total) {
        return;
      }

      setOpenedIndex(index);
      slideToIndex(index);
    },
    [slideToIndex, total]
  );
  // ===== /SWIPER =====

  const open = useCallback(
    (index: number) => {
      setIsOpen(true);
      setOpenedIndex(index);
      slideToIndex(index);
    },
    [slideToIndex]
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setOpenedIndex(null);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        setOpenedIndex(null);

        return false;
      }

      setOpenedIndex((current) => current ?? 0);
      slideToIndex(openedIndex ?? 0);

      return true;
    });
  }, [openedIndex, slideToIndex]);

  const next = useCallback(() => {
    if (openedIndex === null) {
      return;
    }

    const nextIndex = Math.min(openedIndex + 1, total - 1);
    goTo(nextIndex);
  }, [openedIndex, total, goTo]);

  const prev = useCallback(() => {
    if (openedIndex === null) {
      return;
    }

    const prevIndex = Math.max(openedIndex - 1, 0);
    goTo(prevIndex);
  }, [openedIndex, goTo]);

  return {
    // State
    isOpen,
    openedIndex,

    // Actions
    open,
    close,
    toggle,
    next,
    prev,
    goTo,

    // Swiper
    swiperRef,
    thumbsSwiperRef,
    attachSwiper,
    attachThumbsSwiper,
    syncThumbs,
    handleSlideChange,
  };
};
